/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/useMissingTemplateDetails.ts
 * FILE_ID: QA-TEMPLATES-MISSING-DETAILS-001
 * PURPOSE: Fetch template bodies only for the open location while a search is typed.
 */
import { useEffect, type Dispatch, type SetStateAction } from 'react'
import type { QaApi } from '../../../qa/qaApi'
import type { QaTemplateDetail, QaTemplateSummary } from '../../../qa/qaTemplateTypes'
import { BLANK_TEMPLATE_ID, type QaTemplateLocationGroup } from './qaTemplateCatalog'
import { basketForLocation, openTemplateSeed } from './qaTemplateLocationBasket'
import type { QaTemplateSelection } from './useQaTemplatePicker'

export function useMissingTemplateDetails(
  api: Pick<QaApi, 'getTemplate'>,
  query: string,
  locationType: string | null,
  templates: QaTemplateSummary[],
  detailsById: Record<string, QaTemplateDetail>,
  setDetailsById: Dispatch<SetStateAction<Record<string, QaTemplateDetail>>>,
) {
  useEffect(() => {
    const needle = query.trim()
    if (!needle || !locationType) return
    const missing = templates
      .filter((row) => (row.location_type || '') === locationType && row.id !== BLANK_TEMPLATE_ID)
      .map((row) => row.id)
      .filter((id) => !detailsById[id])
    if (missing.length === 0) return
    let cancelled = false
    void Promise.all(missing.map((id) => api.getTemplate(id).catch(() => null))).then((rows) => {
      if (cancelled) return
      setDetailsById((current) => {
        const next = { ...current }
        for (const row of rows) if (row) next[row.id] = row
        return next
      })
    })
    return () => { cancelled = true }
  }, [api, detailsById, locationType, query, setDetailsById, templates])
}

export function checkTemplateLocation({
  locationType,
  on,
  groups,
  detailsById,
  templateId,
  api,
  setBasket,
  setDetailsById,
  clearAll,
  setSelectedSectionIds,
  setSelectedItemIds,
}: {
  locationType: string
  on: boolean
  groups: QaTemplateLocationGroup[]
  detailsById: Record<string, QaTemplateDetail>
  templateId: string | null
  api: Pick<QaApi, 'getTemplate'>
  setBasket: Dispatch<SetStateAction<Record<string, QaTemplateSelection>>>
  setDetailsById: Dispatch<SetStateAction<Record<string, QaTemplateDetail>>>
  clearAll: () => void
  setSelectedSectionIds: Dispatch<SetStateAction<Set<string>>>
  setSelectedItemIds: Dispatch<SetStateAction<Record<string, Set<string>>>>
}) {
  const group = groups.find((row) => row.location_type === locationType)
  if (!group) return
  const apply = (details: Record<string, QaTemplateDetail>) => {
    setBasket((current) => basketForLocation(current, group, details, on))
    if (!templateId || !group.templates.some((template) => template.id === templateId)) return
    if (!on) {
      clearAll()
      return
    }
    const row = details[templateId]
    if (!row) return
    const seeded = openTemplateSeed(row)
    setSelectedSectionIds(seeded.selectedSectionIds)
    setSelectedItemIds(seeded.selectedItemIds)
  }
  const missing = group.templates.filter((row) => row.id !== BLANK_TEMPLATE_ID && !detailsById[row.id])
  if (missing.length === 0) {
    apply(detailsById)
    return
  }
  void Promise.all(missing.map((row) => api.getTemplate(row.id).catch(() => null))).then((loaded) => {
    const details = { ...detailsById }
    for (const row of loaded) if (row) details[row.id] = row
    setDetailsById((current) => ({ ...current, ...details }))
    apply(details)
  })
}
